<script setup>
import { computed } from "vue";
import { Bar } from "vue-chartjs";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const props = defineProps({
  bars: {
    type: Array,
    default: () => [],
  },
  tooltipRows: {
    type: Function,
    default: null,
  },
  showLegend: {
    type: Boolean,
    default: false,
  },
  currentLabel: {
    type: String,
    default: "This Year",
  },
  previousLabel: {
    type: String,
    default: "Previous Year",
  },
});

const currencyFormatter = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  minimumFractionDigits: 0,
});

const normalizedBars = computed(() =>
  props.bars.map((bar) => ({
    label: bar.label,
    current: Number(bar.total ?? 0) / 100,
    previous: Number(bar.previous_total ?? 0) / 100,
  })),
);

const chartData = computed(() => ({
  labels: normalizedBars.value.map((bar) => bar.label),
  datasets: [
    {
      label: props.currentLabel || "This Year",
      backgroundColor: "rgba(245, 158, 11, 0.65)",
      borderColor: "rgba(245, 158, 11, 0.9)",
      borderWidth: 1,
      borderRadius: 6,
      data: normalizedBars.value.map((bar) => bar.current),
    },
    {
      label: props.previousLabel || "Previous Year",
      backgroundColor: "rgba(148, 163, 184, 0.6)",
      borderColor: "rgba(100, 116, 139, 0.9)",
      borderWidth: 1,
      borderRadius: 6,
      data: normalizedBars.value.map((bar) => bar.previous),
    },
  ],
}));

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    x: {
      grid: { display: false },
      ticks: { color: "#64748b", maxRotation: 0, minRotation: 0 },
    },
    y: {
      beginAtZero: true,
      grid: {
        color: "rgba(148, 163, 184, 0.25)",
        drawBorder: false,
      },
      ticks: {
        color: "#64748b",
        callback: (value) => currencyFormatter.format(value),
      },
    },
  },
  plugins: {
    legend: { display: props.showLegend },
    tooltip: {
      callbacks: {
        label: (context) =>
          `${context.dataset.label}: ${currencyFormatter.format(context.parsed.y ?? 0)}`,
        afterBody: (tooltipItems) => {
          if (!props.tooltipRows) {
            return [];
          }
          return props.tooltipRows(tooltipItems) || [];
        },
      },
    },
  },
}));
</script>

<template>
  <div class="w-full">
    <Bar :data="chartData" :options="chartOptions" class="h-48" />
  </div>
</template>
